/**
 * Сервис-слой для работы с "БД". Сейчас — localStorage + in-memory, позже
 * меняются только внутренности методов на axios. Сигнатуры API стабильны.
 *
 * Иерархия: Sphere → Theme → Subtheme → SemanticUnit → Angle → Ad.
 */

import { DbDatabase, emptyDb, DbOffer, DbOfferBinding, OfferBindingScope, DbSchoolImage, migrateDbShape } from './types';
import { LifeSphere, Theme, Subtheme, SemanticUnit, Product, Angle, Ad, AdCabinet, Campaign, AdGroup, CampaignAd, AutoModeSettings } from '../types';

const STORAGE_KEY = 'products.db.v2';

type Listener = () => void;
const listeners = new Set<Listener>();

const load = (): DbDatabase | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return migrateDbShape(parsed);
  } catch {
    return null;
  }
};

const save = (next: DbDatabase) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* quota */
  }
};

let _db: DbDatabase | null = null;

/** Лениво инициализирует БД из localStorage или seed. Сиды подгружаются по требованию. */
const ensure = (): DbDatabase => {
  if (_db) return _db;
  const loaded = load();
  if (loaded) {
    _db = loaded;
    return _db;
  }
  // require вместо import — чтобы избежать циклической зависимости на этапе
  // инициализации модуля.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { seedDonskoj } = require('./seed');
  _db = seedDonskoj(emptyDb());
  save(_db!);
  return _db!;
};

const notify = () => listeners.forEach((l) => l());

export const subscribe = (fn: Listener) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

const touch = () => { if (_db) save(_db); notify(); };

const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
const now = () => new Date().toISOString();

// --- Admin ---

export const resetDb = () => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { seedDonskoj } = require('./seed');
  _db = seedDonskoj(emptyDb());
  touch();
};

export const clearDb = () => {
  _db = emptyDb();
  touch();
};

export const getDb = (): DbDatabase => ensure();

// --- Spheres / Themes / Subthemes / Units ---

export const listSpheres = (productId: string) => {
  const db = ensure();
  return db.spheres.filter((s) => s.productId === productId).sort((a, b) => a.displayOrder - b.displayOrder);
};

export const listThemes = (sphereId: string) => {
  const db = ensure();
  return db.themes.filter((t) => t.sphereId === sphereId).sort((a, b) => a.displayOrder - b.displayOrder);
};

export const listSubthemes = (themeId: string) => {
  const db = ensure();
  return db.subthemes.filter((s) => s.themeId === themeId).sort((a, b) => a.displayOrder - b.displayOrder);
};

export const listUnits = (subthemeId: string) => {
  const db = ensure();
  return db.semanticUnits.filter((u) => u.subthemeId === subthemeId).sort((a, b) => a.displayOrder - b.displayOrder);
};

/** Собрать LifeSphere[] для UI из плоских таблиц. */
export const getSpheresNested = (productId: string): LifeSphere[] => {
  return listSpheres(productId).map((s) => ({
    id: s.id,
    productId: s.productId,
    name: s.name,
    description: s.description || '',
    status: s.status,
    priority: s.priority,
    history: s.history,
    themes: listThemes(s.id).map<Theme>((t) => ({
      id: t.id,
      sphereId: t.sphereId,
      name: t.name,
      description: t.description || '',
      keywords: t.keywords,
      images: t.images,
      status: t.status,
      priority: t.priority,
      history: t.history,
      subthemes: listSubthemes(t.id).map<Subtheme>((st) => ({
        id: st.id,
        themeId: st.themeId,
        name: st.name,
        description: st.description || '',
        keywords: st.keywords,
        status: st.status,
        priority: st.priority,
        history: st.history,
        units: listUnits(st.id).map<SemanticUnit>((u) => ({
          id: u.id,
          subthemeId: u.subthemeId,
          name: u.name,
          description: u.description || '',
          examples: u.examples || [],
          keywords: u.keywords,
          status: u.status,
          priority: u.priority,
          history: u.history,
        })),
      })),
    })),
  }));
};

/** Обратная операция: принять LifeSphere[] и перезаписать плоские таблицы для productId. */
export const replaceSpheresNested = (productId: string, nested: LifeSphere[]) => {
  const db = ensure();
  db.spheres = db.spheres.filter((s) => s.productId !== productId);
  const remainingSphereIds = new Set(db.spheres.map((s) => s.id));
  db.themes = db.themes.filter((t) => remainingSphereIds.has(t.sphereId));
  const remainingThemeIds = new Set(db.themes.map((t) => t.id));
  db.subthemes = db.subthemes.filter((s) => remainingThemeIds.has(s.themeId));
  const remainingSubthemeIds = new Set(db.subthemes.map((s) => s.id));
  db.semanticUnits = db.semanticUnits.filter((u) => remainingSubthemeIds.has(u.subthemeId));

  nested.forEach((s, si) => {
    db.spheres.push({
      id: s.id, productId: s.productId, name: s.name, description: s.description,
      status: s.status, priority: s.priority, history: s.history,
      displayOrder: si, createdAt: now(),
    });
    s.themes.forEach((t, ti) => {
      db.themes.push({
        id: t.id, sphereId: t.sphereId, name: t.name, description: t.description,
        keywords: t.keywords, images: t.images,
        status: t.status, priority: t.priority, history: t.history,
        displayOrder: ti, createdAt: now(),
      });
      t.subthemes.forEach((st, sti) => {
        db.subthemes.push({
          id: st.id, themeId: st.themeId, name: st.name, description: st.description,
          keywords: st.keywords,
          status: st.status, priority: st.priority, history: st.history,
          displayOrder: sti, createdAt: now(),
        });
        st.units.forEach((u, ui) => {
          db.semanticUnits.push({
            id: u.id, subthemeId: u.subthemeId, name: u.name, description: u.description,
            examples: u.examples, keywords: u.keywords,
            status: u.status, priority: u.priority, history: u.history,
            displayOrder: ui, createdAt: now(),
          });
        });
      });
    });
  });
  touch();
};

// --- Offers ---

export const listOffers = (productId: string): DbOffer[] =>
  ensure().offers.filter((o) => o.productId === productId);

export const listOfferBindings = (offerId: string): DbOfferBinding[] =>
  ensure().offerBindings.filter((b) => b.offerId === offerId);

export const createOffer = (data: Omit<DbOffer, 'id' | 'createdAt'>): DbOffer => {
  const db = ensure();
  const offer: DbOffer = { ...data, id: uid(), createdAt: now() };
  db.offers.push(offer);
  touch();
  return offer;
};

export const updateOffer = (id: string, patch: Partial<Omit<DbOffer, 'id' | 'productId' | 'createdAt'>>) => {
  const db = ensure();
  const i = db.offers.findIndex((o) => o.id === id);
  if (i < 0) return;
  db.offers[i] = { ...db.offers[i], ...patch, updatedAt: now() };
  touch();
};

export const deleteOffer = (id: string) => {
  const db = ensure();
  db.offers = db.offers.filter((o) => o.id !== id);
  db.offerBindings = db.offerBindings.filter((b) => b.offerId !== id);
  db.offerAddresses = db.offerAddresses.filter((a) => a.offerId !== id);
  db.offerRkBindings = db.offerRkBindings.filter((r) => r.offerId !== id);
  touch();
};

export const setOfferBindings = (offerId: string, bindings: { scope: OfferBindingScope; scopeId: string }[]) => {
  const db = ensure();
  db.offerBindings = db.offerBindings.filter((b) => b.offerId !== offerId);
  bindings.forEach((b) => {
    db.offerBindings.push({ id: uid(), offerId, scope: b.scope, scopeId: b.scopeId, createdAt: now() });
  });
  touch();
};

// --- Products ---
export const listProducts = (): Product[] => ensure().products;
export const setProducts = (next: Product[]) => { ensure().products = next; touch(); };

// --- Angles ---
export const listAngles = (): Angle[] => ensure().angles;
export const setAngles = (next: Angle[]) => { ensure().angles = next; touch(); };

// --- Ads ---
export const listAds = (): Ad[] => ensure().ads;
export const setAds = (next: Ad[]) => { ensure().ads = next; touch(); };

// --- Ad cabinets ---
export const listAdCabinets = (): AdCabinet[] => ensure().adCabinets;
export const setAdCabinets = (next: AdCabinet[]) => { ensure().adCabinets = next; touch(); };

export const listProductCabinetIds = (productId: string): string[] =>
  ensure().productCabinetLinks.filter((l) => l.productId === productId).map((l) => l.cabinetId);

export const setProductCabinetIds = (productId: string, cabinetIds: string[]) => {
  const db = ensure();
  db.productCabinetLinks = db.productCabinetLinks.filter((l) => l.productId !== productId);
  cabinetIds.forEach((cabinetId) => db.productCabinetLinks.push({ productId, cabinetId }));
  touch();
};

// --- Campaigns ---
export const listCampaigns = (productId: string): Campaign[] =>
  ensure().campaigns.filter((c) => c.productId === productId);

export const setCampaigns = (productId: string, next: Campaign[]) => {
  const db = ensure();
  db.campaigns = [...db.campaigns.filter((c) => c.productId !== productId), ...next];
  touch();
};

// --- Ad groups (глобально) ---
export const listAdGroups = (): AdGroup[] => ensure().adGroups;
export const setAdGroups = (next: AdGroup[]) => { ensure().adGroups = next; touch(); };

// --- Campaign ads (глобально) ---
export const listCampaignAds = (): CampaignAd[] => ensure().campaignAds;
export const setCampaignAds = (next: CampaignAd[]) => { ensure().campaignAds = next; touch(); };

// --- School images (общая библиотека на уровне школы) ---
export const listSchoolImages = (schoolId: string): DbSchoolImage[] =>
  ensure().schoolImages.filter((i) => i.schoolId === schoolId);

export const addSchoolImage = (img: Omit<DbSchoolImage, 'id' | 'createdAt'>): DbSchoolImage => {
  const db = ensure();
  const created: DbSchoolImage = { ...img, id: uid(), createdAt: now() };
  db.schoolImages.push(created);
  touch();
  return created;
};

export const deleteSchoolImage = (id: string) => {
  const db = ensure();
  db.schoolImages = db.schoolImages.filter((i) => i.id !== id);
  touch();
};

// --- Auto mode settings ---
export const getAutoModeSettings = (productId: string): AutoModeSettings | undefined =>
  ensure().autoModeSettings.find((s) => s.productId === productId);

export const saveAutoModeSettings = (settings: AutoModeSettings) => {
  const db = ensure();
  const i = db.autoModeSettings.findIndex((s) => s.productId === settings.productId);
  if (i < 0) db.autoModeSettings.push(settings);
  else db.autoModeSettings[i] = { ...settings, updatedAt: now() };
  touch();
};
